/* ==========================================================================
   THE BRAND ALKEMI — INTERACTIVE LAYER
   assets/alkemi-interactive.js
   1. Three.js Ouroboros hero (tapered wireframe loop + cone head + ghost dupe)
   2. Mouse drag + native touch rotation (passive listeners) + auto-rotate
   3. Responsive canvas resize (orientation flips / split-screen)
   4. Looping 24-hour drop countdown timer
   ========================================================================== */

(function () {
  'use strict';

  /* Guard: this asset may be requested by more than one section.
     The browser caches the file, but if it executes twice, bail early. */
  if (window.__ALKEMI_INTERACTIVE_LOADED__) {
    return;
  }
  window.__ALKEMI_INTERACTIVE_LOADED__ = true;

  /* ------------------------------------------------------------------
     UTILITY — reduced motion preference
     ------------------------------------------------------------------ */
  var prefersReducedMotion = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ==================================================================
     PART 1 — OUROBOROS 3D HERO
     ================================================================== */

  /**
   * Builds a tapered tube that follows a near-complete circle.
   * The tube starts thick (the body behind the head) and tapers
   * to a point at the tail. Low polycount: 72 path segments x 10
   * radial segments keeps this comfortably at 60fps on mid-tier
   * mobile GPUs.
   */
  function buildTaperedLoopGeometry(loopRadius, maxTubeRadius, pathSegments, radialSegments, arcStart, arcEnd) {
    var positions = [];
    var indices = [];
    var i, j, t, angle, tubeRadius;
    var cx, cy, theta, nx, ny, px, py, pz;

    for (i = 0; i <= pathSegments; i++) {
      t = i / pathSegments;
      angle = arcStart + (arcEnd - arcStart) * t;
      cx = Math.cos(angle) * loopRadius;
      cy = Math.sin(angle) * loopRadius;

      /* Taper: full radius at the head end (t = 0),
         collapsing to a sharp point at the tail (t = 1). */
      tubeRadius = Math.max(0.015, maxTubeRadius * Math.pow(1 - t, 0.85));

      /* Ring frame: radial normal in the XY plane, binormal on Z. */
      nx = Math.cos(angle);
      ny = Math.sin(angle);

      for (j = 0; j <= radialSegments; j++) {
        theta = (j / radialSegments) * Math.PI * 2;
        px = cx + nx * Math.cos(theta) * tubeRadius;
        py = cy + ny * Math.cos(theta) * tubeRadius;
        pz = Math.sin(theta) * tubeRadius;
        positions.push(px, py, pz);
      }
    }

    /* Stitch the rings together into a triangle grid. */
    var ringSize = radialSegments + 1;
    var a, b, c, d;
    for (i = 0; i < pathSegments; i++) {
      for (j = 0; j < radialSegments; j++) {
        a = i * ringSize + j;
        b = a + 1;
        c = a + ringSize;
        d = c + 1;
        indices.push(a, c, b);
        indices.push(b, c, d);
      }
    }

    var geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();
    return geometry;
  }

  /**
   * Assembles one full ouroboros: tapered body + cone head
   * angled back across the gap to bite the tail.
   */
  function buildOuroboros(color, opacity) {
    var loopRadius = 1.6;
    var maxTubeRadius = 0.3;
    var arcStart = 0;
    var arcEnd = Math.PI * 2 * 0.93; /* leaves a small gap for the bite */

    var group = new THREE.Group();

    var material = new THREE.MeshBasicMaterial({
      color: color,
      wireframe: true,
      transparent: opacity < 1,
      opacity: opacity
    });

    /* Body */
    var bodyGeometry = buildTaperedLoopGeometry(loopRadius, maxTubeRadius, 72, 10, arcStart, arcEnd);
    var body = new THREE.Mesh(bodyGeometry, material);
    group.add(body);

    /* Head: a cone at the arc start, pointing across the gap
       toward the tail tip so the loop closes on itself. */
    var headGeometry = new THREE.ConeGeometry(maxTubeRadius * 1.55, maxTubeRadius * 3.1, 9, 1, false);
    var head = new THREE.Mesh(headGeometry, material);

    var headBase = new THREE.Vector3(
      Math.cos(arcStart) * loopRadius,
      Math.sin(arcStart) * loopRadius,
      0
    );
    var tailTip = new THREE.Vector3(
      Math.cos(arcEnd) * loopRadius,
      Math.sin(arcEnd) * loopRadius,
      0
    );

    /* Orient the cone (default +Y axis) toward the tail tip. */
    var direction = new THREE.Vector3().subVectors(tailTip, headBase).normalize();
    var quaternion = new THREE.Quaternion().setFromUnitVectors(
      new THREE.Vector3(0, 1, 0),
      direction
    );
    head.quaternion.copy(quaternion);

    /* Sit the head just past the body start, reaching into the gap. */
    head.position.copy(headBase).addScaledVector(direction, maxTubeRadius * 1.4);
    group.add(head);

    return group;
  }

  function initHero() {
    var canvas = document.querySelector('[data-alkemi-canvas]');
    if (!canvas || canvas.dataset.alkemiInitialized === 'true') {
      return true; /* nothing to do */
    }
    if (typeof THREE === 'undefined') {
      return false; /* Three.js not ready yet — caller will retry */
    }
    canvas.dataset.alkemiInitialized = 'true';

    var container = canvas.parentElement;

    /* ------ Scene / camera / renderer ------ */
    var scene = new THREE.Scene();

    var camera = new THREE.PerspectiveCamera(
      45,
      container.clientWidth / container.clientHeight,
      0.1,
      100
    );

    /* Aspect-aware camera distance: on narrow portrait screens the
       camera pulls back so the full loop (radius 1.6 + tube 0.3,
       plus breathing room) always fits the viewport width. */
    var LOOP_HALF_EXTENT = 2.1;
    var BASE_CAMERA_Z = 5.4;
    var HALF_FOV_TAN = Math.tan((45 / 2) * Math.PI / 180);

    function fitCamera() {
      var aspect = camera.aspect;
      var requiredZ = LOOP_HALF_EXTENT / (HALF_FOV_TAN * Math.max(aspect, 0.1));
      camera.position.set(0, 0, Math.max(BASE_CAMERA_Z, requiredZ));
    }
    fitCamera();

    var renderer = new THREE.WebGLRenderer({
      canvas: canvas,
      alpha: true,
      antialias: true,
      powerPreference: 'high-performance'
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(container.clientWidth, container.clientHeight);

    /* ------ Lighting (kept basic for performance) ------ */
    var ambient = new THREE.AmbientLight(0xffffff, 0.8);
    scene.add(ambient);
    var directional = new THREE.DirectionalLight(0xffffff, 0.6);
    directional.position.set(2, 3, 4);
    scene.add(directional);

    /* ------ Meshes: signature red primary + faint white ghost ------ */
    var rig = new THREE.Group();

    var primary = buildOuroboros(0xE8332B, 1);
    rig.add(primary);

    var ghost = buildOuroboros(0xF5F3EE, 0.16);
    ghost.position.z = -1.1;
    ghost.rotation.z = 0.25;
    rig.add(ghost);

    /* Tilt slightly so the loop reads as a 3D object immediately. */
    rig.rotation.x = 0.45;
    scene.add(rig);

    /* ------ Interaction state ------ */
    var isDragging = false;
    var lastX = 0;
    var lastY = 0;
    var velocityY = 0;
    var velocityX = 0;
    var targetRotY = 0;
    var targetRotX = 0.45;
    var DRAG_FACTOR = 0.005;
    var AUTO_ROTATE_SPEED = prefersReducedMotion ? 0 : 0.0035;

    function onDragStart(x, y) {
      isDragging = true;
      lastX = x;
      lastY = y;
      velocityY = 0;
      velocityX = 0;
    }

    function onDragMove(x, y) {
      if (!isDragging) return;
      var deltaX = x - lastX;
      var deltaY = y - lastY;
      lastX = x;
      lastY = y;
      targetRotY += deltaX * DRAG_FACTOR;
      targetRotX += deltaY * DRAG_FACTOR;
      /* Clamp the pitch so the loop never flips upside down. */
      targetRotX = Math.max(-1.2, Math.min(1.4, targetRotX));
      velocityY = deltaX * DRAG_FACTOR;
      velocityX = deltaY * DRAG_FACTOR;
    }

    function onDragEnd() {
      isDragging = false;
    }

    /* Mouse (desktop) */
    canvas.addEventListener('mousedown', function (event) {
      onDragStart(event.clientX, event.clientY);
    });
    window.addEventListener('mousemove', function (event) {
      onDragMove(event.clientX, event.clientY);
    });
    window.addEventListener('mouseup', onDragEnd);

    /* Native touch (mobile / tablet) — passive: true for
       immediate, hardware-accelerated, non-blocking response. */
    canvas.addEventListener('touchstart', function (event) {
      if (event.touches.length === 1) {
        onDragStart(event.touches[0].clientX, event.touches[0].clientY);
      }
    }, { passive: true });

    canvas.addEventListener('touchmove', function (event) {
      if (event.touches.length === 1) {
        onDragMove(event.touches[0].clientX, event.touches[0].clientY);
      }
    }, { passive: true });

    canvas.addEventListener('touchend', onDragEnd, { passive: true });
    canvas.addEventListener('touchcancel', onDragEnd, { passive: true });

    /* ------ Responsive resize: orientation flips, split-screen ------ */
    function handleResize() {
      var width = container.clientWidth;
      var height = container.clientHeight;
      if (width === 0 || height === 0) return;
      camera.aspect = width / height;
      fitCamera();
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    }
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    /* ------ Render loop with visibility-aware pausing ------
       The loop only runs while the hero is actually onscreen and the
       tab is visible: scrolling past the hero on mobile stays at a
       clean 60fps and the GPU stops draining battery. */
    var isRunning = false;
    var heroVisible = true;

    function renderFrame() {
      if (!isRunning) return;
      requestAnimationFrame(renderFrame);

      /* Auto-rotation always ticks underneath user input. */
      targetRotY += AUTO_ROTATE_SPEED;

      /* Inertia after release. */
      if (!isDragging) {
        targetRotY += velocityY;
        targetRotX += velocityX;
        targetRotX = Math.max(-1.2, Math.min(1.4, targetRotX));
        velocityY *= 0.94;
        velocityX *= 0.94;
      }

      /* Smooth damping toward the target orientation. */
      rig.rotation.y += (targetRotY - rig.rotation.y) * 0.08;
      rig.rotation.x += (targetRotX - rig.rotation.x) * 0.08;

      renderer.render(scene, camera);
    }

    function startLoop() {
      if (isRunning) return;
      isRunning = true;
      renderFrame();
    }

    function stopLoop() {
      isRunning = false;
    }

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        heroVisible = entries[0].isIntersecting;
        if (heroVisible && !document.hidden) {
          startLoop();
        } else {
          stopLoop();
        }
      }, { threshold: 0 });
      observer.observe(canvas);
    }

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        stopLoop();
      } else if (heroVisible) {
        startLoop();
      }
    });

    startLoop();

    return true;
  }

  /* Three.js loads via its own deferred CDN tag. Depending on section
     order in the template, this file can execute first, so retry
     briefly until THREE exists. */
  function bootHero() {
    if (initHero()) return;
    var attempts = 0;
    var retry = window.setInterval(function () {
      attempts += 1;
      if (initHero() || attempts > 40) {
        window.clearInterval(retry);
      }
    }, 250);
  }

  /* ==================================================================
     PART 2 — 24-HOUR LOOPING DROP COUNTDOWN
     Counts down to local midnight, then restarts — a perpetual
     active launch window.
     ================================================================== */
  function pad(value) {
    return value < 10 ? '0' + value : String(value);
  }

  function initCountdowns() {
    var timers = document.querySelectorAll('[data-alkemi-countdown]');
    if (!timers.length) return;

    function tick() {
      var now = new Date();
      var next = new Date(now);
      next.setHours(24, 0, 0, 0); /* upcoming local midnight */
      var diff = Math.max(0, next.getTime() - now.getTime());

      var hours = Math.floor(diff / 3600000);
      var minutes = Math.floor((diff % 3600000) / 60000);
      var seconds = Math.floor((diff % 60000) / 1000);
      var display = pad(hours) + ':' + pad(minutes) + ':' + pad(seconds);

      for (var i = 0; i < timers.length; i++) {
        timers[i].textContent = display;
      }
    }

    tick();
    window.setInterval(tick, 1000);
  }

  /* ==================================================================
     BOOT
     ================================================================== */
  function boot() {
    bootHero();
    initCountdowns();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
